import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, App, AutoComplete, Avatar, Button, DatePicker, Descriptions, Empty, Form, Grid, Input, Modal,
  Radio, Select, Space, Spin, Table, Tag, Tooltip, Typography,
} from 'antd';
import {
  DeleteOutlined, HistoryOutlined, LeftOutlined, PlusOutlined, ReloadOutlined, RightOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  DURUM_RENK, FisDugmesi, GUNLER, GecmisListesi, IsaretBandi, IsaretEtiketleri, OGUN_ADI, ciddiMi, gunAdi, gunTutari,
  TIP_ADI, TIP_RENK, hata, kisaPara, kisaTarih, para, pazartesi, tarih, useHarcirahApi,
} from './ortak';
import { GiderBolumu, ULASIM_TANIM } from './giderler';

/**
 * Seyahat planı parçaları (23.10.2026; plan: "Harcırah Sistemi Planı", ATM Point Yönetim, 15.10.2026).
 * Haftalık plan ve seyahat penceresi: seyahati REZERVASYON PERSONELİ açar (24.10.2026 ekran ayrımı;
 * /harcirah-rezervasyon, rezervasyon personeli) — kişi, il, tarih, tür, tip (A = öğle + akşam, B = yalnız akşam, C = yalnız öğle), amir, ulaşım;
 * tip/amir/il kişinin son seyahatinden ön dolu gelir. Her plan değişikliğinde neden zorunlu, geçmişe yazılır.
 * Personel sekmesi ve salt okunur seyahat penceresi yönetim ekranında (/harcirah, Harcırah r).
 */
const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const NEDENLER = [
  'Gidiş günü öğleden sonra yola çıkıldı',
  'Dönüş günü öğleden önce dönüldü',
  'Yemek firma / müşteri tarafından karşılandı',
  'Seyahat planı değişti',
].map((value) => ({ value }));
const nedenFiltre = (girdi, o) => o.value.toLocaleLowerCase('tr').includes(girdi.toLocaleLowerCase('tr'));

// --- Neden penceresi --------------------------------------------------------------------------
// istek: { baslik, aciklama, onOk(neden) => Promise } | null. onOk hatasını kendisi gösterir.
function NedenPenceresi({ istek, onKapat }) {
  const [neden, setNeden] = useState('');
  const [bekliyor, setBekliyor] = useState(false);
  useEffect(() => { setNeden(''); }, [istek]);
  const tamam = () => {
    setBekliyor(true);
    istek.onOk(neden.trim()).finally(() => { setBekliyor(false); onKapat(); });
  };
  return (
    <Modal rootClassName="hr-modal" open={!!istek} title={istek?.baslik} onCancel={onKapat} onOk={tamam} okText="Kaydet" cancelText="Vazgeç"
      okButtonProps={{ disabled: neden.trim().length < 3 }} confirmLoading={bekliyor} destroyOnHidden>
      {istek?.aciklama && <p style={{ marginBottom: 8 }}>{istek.aciklama}</p>}
      <AutoComplete autoFocus value={neden} onChange={setNeden} options={NEDENLER} filterOption={nedenFiltre}
        style={{ width: '100%' }} placeholder="Değişiklik Nedeni (Zorunlu)" />
    </Modal>
  );
}

// --- Gün tablosu (yönetici görünümü: hak + çalışanın girdiği tutar + fiş) ---------------------
function GunTablosu({ s }) {
  const ogunHucresi = (g, o) => {
    if (!g[o]) return <Text type="danger">Yok</Text>;
    const t = g[`${o}_tutar`];
    if (t == null) return <Text type="secondary">Girilmedi</Text>;
    return (
      <div>
        <Space size={0}>{para(t)}<FisDugmesi seyahatId={s.id} gunTarihi={g.tarih} ogun={o} fis={g[`${o}_fis`]} /></Space>
        <IsaretEtiketleri isaretler={s.isaretler?.[`${g.tarih}|${o}`]} />
      </div>
    );
  };
  const gunCiddi = (g) => ['ogle', 'aksam'].some((o) => ciddiMi(s.isaretler?.[`${g.tarih}|${o}`]));
  return (
    <Table size="small" pagination={false} rowKey="tarih" dataSource={s.gunler} style={{ marginBottom: 12 }}
      rowClassName={(g) => (gunCiddi(g) ? 'hr-isaretli-satir' : '')}
      columns={[
        { title: 'Gün', dataIndex: 'tarih', render: (v) => `${gunAdi(v)} ${tarih(v)}` },
        { title: 'Öğle', render: (_, g) => ogunHucresi(g, 'ogle') },
        { title: 'Akşam', render: (_, g) => ogunHucresi(g, 'aksam') },
        { title: 'Gün Toplamı', align: 'right', render: (_, g) => (gunTutari(g) ? para(gunTutari(g)) : '') },
      ]}
      summary={() => (
        <Table.Summary.Row>
          <Table.Summary.Cell index={0}><Text strong>Toplam</Text></Table.Summary.Cell>
          <Table.Summary.Cell index={1} colSpan={2} align="right"><Text type="secondary">{s.girilen_ogun} / {s.acik_ogun} Öğün Girildi</Text></Table.Summary.Cell>
          <Table.Summary.Cell index={3} align="right"><Text strong>{para(s.toplam)}</Text></Table.Summary.Cell>
        </Table.Summary.Row>
      )}
    />
  );
}

// --- Seyahat penceresi (ekle / düzenle) -------------------------------------------------------
export function SeyahatPenceresi({
  acik, seyahatId, varsayilanHafta, secenekler, onKapat, onKaydedildi, kok = '/harcirah-rezervasyon', yazabilir = true,
}) {
  const api = useHarcirahApi(kok);
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [nedenIstegi, setNedenIstegi] = useState(null);
  const [seyahat, setSeyahat] = useState(null);
  const [onceki, setOnceki] = useState(null);
  const [yukleniyor, setYukleniyor] = useState(false);
  const [kaydediliyor, setKaydediliyor] = useState(false);
  const duzenleme = !!seyahatId;
  const kisi = Form.useWatch('kullanici_no', form);

  useEffect(() => {
    if (!acik) return;
    form.resetFields();
    setOnceki(null);
    setSeyahat(null);
    if (seyahatId) {
      setYukleniyor(true);
      api.get(`/${seyahatId}`).then((s) => {
        setSeyahat(s);
        form.setFieldsValue({
          kullanici_no: s.kullanici_no, amir: s.amir, il_no: s.il_no, aciklama: s.aciklama, tip: s.tip,
          seyahat_turu: s.seyahat_turu || undefined,
          tarihler: [dayjs(s.baslangic), dayjs(s.bitis)], neden: '',
        });
      }).catch((e) => message.error(hata(e))).finally(() => setYukleniyor(false));
    } else if (varsayilanHafta) {
      form.setFieldsValue({ tarihler: [varsayilanHafta, varsayilanHafta.add(4, 'day')] });
    }
  }, [acik, seyahatId]); // eslint-disable-line react-hooks/exhaustive-deps

  const kisiSecildi = (k) => {
    if (!k) return;
    api.get(`/onceki/${k}`).then((o) => {
      setOnceki(o);
      if (o) form.setFieldsValue({ tip: o.tip, amir: o.amir, il_no: o.il_no, aciklama: o.aciklama });
      else form.setFieldsValue({ tip: undefined, amir: undefined });
    }).catch(() => {});
  };

  const kaydet = () => form.validateFields().then((v) => {
    const govde = {
      amir: v.amir, il_no: v.il_no, aciklama: v.aciklama || '', tip: v.tip, seyahat_turu: v.seyahat_turu,
      baslangic: v.tarihler[0].format('YYYY-MM-DD'), bitis: v.tarihler[1].format('YYYY-MM-DD'),
    };
    setKaydediliyor(true);
    const istek = duzenleme
      ? api.put(`/${seyahatId}`, { ...govde, neden: v.neden })
      : api.post('', { ...govde, kullanici_no: v.kullanici_no, ulasim: v.ulasim || null });
    return istek.then(() => {
      message.success(duzenleme ? 'Seyahat güncellendi.' : 'Seyahat eklendi.');
      onKaydedildi();
    }).catch((e) => message.error(hata(e))).finally(() => setKaydediliyor(false));
  }).catch(() => {});

  const sil = () => setNedenIstegi({
    baslik: 'Seyahati Sil',
    aciklama: `${seyahat.personel_adi} · ${seyahat.il_ad} · ${tarih(seyahat.baslangic)}–${tarih(seyahat.bitis)}`,
    onOk: (neden) => api.del(`/${seyahatId}`, { neden })
      .then(() => { message.success('Seyahat silindi.'); onKaydedildi(); })
      .catch((e) => message.error(hata(e))),
  });

  const salt = !yazabilir || (duzenleme && seyahat && !seyahat.duzenlenebilir);
  const kisiSecenekleri = (secenekler?.personel || []).map((p) => ({
    value: p.kullanici_no, label: p.pozisyon ? `${p.ad_soyad} — ${p.pozisyon}` : p.ad_soyad,
  }));

  return (
    <Modal rootClassName="hr-modal"
      open={acik}
      title={duzenleme ? 'Seyahat' : 'Seyahat Ekle'}
      onCancel={onKapat}
      width={760}
      destroyOnHidden
      footer={[
        duzenleme && yazabilir && seyahat?.duzenlenebilir && (
          <Button key="sil" danger icon={<DeleteOutlined />} onClick={sil} style={{ float: 'left' }}>Sil</Button>
        ),
        <Button key="kapat" onClick={onKapat}>{salt ? 'Kapat' : 'Vazgeç'}</Button>,
        !salt && <Button key="kaydet" type="primary" loading={kaydediliyor} onClick={kaydet}>Kaydet</Button>,
      ].filter(Boolean)}
    >
      <Spin spinning={yukleniyor}>
        {salt && seyahat && yazabilir && (
          <Alert type="info" showIcon style={{ marginBottom: 12 }}
            message={`Seyahat "${seyahat.durum_adi}" durumunda; değiştirilemez.`} />
        )}
        <Form form={form} layout="vertical" disabled={salt} requiredMark="optional">
          <Form.Item name="kullanici_no" label="Personel" rules={[{ required: true, message: 'Personel seçin' }]}>
            <Select showSearch optionFilterProp="label" options={kisiSecenekleri} disabled={duzenleme}
              placeholder="Seyahate Çıkan Kişi" onChange={kisiSecildi} />
          </Form.Item>
          {!duzenleme && kisi && (
            onceki
              ? <Alert type="success" showIcon style={{ marginBottom: 12 }}
                  message={`Tip, amir ve il son seyahatinden (${tarih(onceki.baslangic)}–${tarih(onceki.bitis)}) dolduruldu.`} />
              : <Alert type="info" showIcon style={{ marginBottom: 12 }}
                  message="İlk seyahati: tip ve amiri seçin; sonraki seyahatlerine buradan otomatik gelir." />
          )}
          <Form.Item name="seyahat_turu" label="Seyahat Türü" rules={[{ required: true, message: 'Seyahat türünü seçin' }]}>
            <Radio.Group optionType="button" buttonStyle="solid" options={secenekler?.seyahat_turleri || []}
              style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }} />
          </Form.Item>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <Form.Item name="il_no" label="Gidilen İl" style={{ flex: '1 1 200px' }}
              rules={[{ required: true, message: 'İl seçin' }]}>
              <Select showSearch optionFilterProp="label"
                options={(secenekler?.iller || []).map((i) => ({ value: i.il_no, label: i.il_ad }))} />
            </Form.Item>
            <Form.Item name="aciklama" label="Açıklama" style={{ flex: '2 1 300px' }}>
              <Input maxLength={250} placeholder="Yer / iş (ör. enerji lokasyonları)" />
            </Form.Item>
          </div>
          <Form.Item name="tarihler" label="Başlangıç – Bitiş" rules={[{ required: true, message: 'Tarih seçin' }]}>
            <RangePicker format="DD.MM.YYYY" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="tip" label="Harcırah Tipi" rules={[{ required: true, message: 'Tip seçin' }]}
            extra={duzenleme && !salt ? 'Tip değişirse tüm günlerin öğün hakları yeni tipe göre yeniden açılır (gün bazındaki değişiklikler silinir).' : null}>
            <Radio.Group optionType="button" buttonStyle="solid" options={secenekler?.tipler || []} style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }} />
          </Form.Item>
          {!duzenleme && (
            <Form.Item name="ulasim" label="Ulaşım" extra="Uçak bileti, otel ve araç kiralamayı Rezervasyonlar sekmesinden girersiniz; yakıt fişlerini çalışan girer.">
              <Radio.Group optionType="button" buttonStyle="solid"
                options={Object.entries(ULASIM_TANIM).map(([k, u]) => ({ value: k, label: <span>{u.ikon} {u.ad}</span> }))} />
            </Form.Item>
          )}
          <Form.Item name="amir" label="Amir (Onaylayacak Kişi)" dependencies={['kullanici_no']}
            rules={[{ required: true, message: 'Amir seçin' },
              ({ getFieldValue }) => ({
                validator: (_, v) => (v && v === getFieldValue('kullanici_no')
                  ? Promise.reject(new Error('Kişi kendi seyahatinin amiri olamaz.')) : Promise.resolve()),
              })]}>
            <Select showSearch optionFilterProp="label" options={kisiSecenekleri} />
          </Form.Item>
          {duzenleme && !salt && (
            <Form.Item name="neden" label="Değişiklik Nedeni"
              rules={[{ required: true, min: 3, message: 'Neden yazın' }]}>
              <AutoComplete options={NEDENLER} placeholder="Neden (Zorunlu)" filterOption={nedenFiltre} />
            </Form.Item>
          )}
        </Form>
        {seyahat && (
          <>
            <Descriptions size="small" column={3} bordered style={{ marginBottom: 12 }}
              items={[
                { label: 'Durum', children: <Tag color={DURUM_RENK[seyahat.durum]}>{seyahat.durum_adi}</Tag> },
                { label: 'Tür', children: seyahat.seyahat_turu_adi },
                { label: 'Gün', children: seyahat.gunler.length },
                { label: 'Yemek Harcırahı', children: <Text strong>{para(seyahat.toplam)}</Text> },
                { label: 'Şirket Giderleri', children: para(seyahat.gider_toplam) },
                { label: 'Seyahat Maliyeti', children: <Text strong>{para(seyahat.maliyet)}</Text> },
                ...(seyahat.rez_notu ? [{ label: 'Rezervasyon Talebi', span: 3, children: seyahat.rez_notu }] : []),
              ]} />
            <Title level={5}>Çalışanın Girdiği Tutarlar</Title>
            <IsaretBandi isaretler={seyahat.isaretler} giderIsaretleri={seyahat.gider_isaretleri} />
            <GunTablosu s={seyahat} />
            <Title level={5}>Ulaşım Ve Konaklama <Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>· şirket öder; rezervasyonlar Rezervasyonlar sekmesinden</Text></Title>
            <GiderBolumu s={seyahat} baslik={false} />
            <Title level={5}><HistoryOutlined /> Geçmiş</Title>
            <GecmisListesi gecmis={seyahat.gecmis} />
          </>
        )}
      </Spin>
      <NedenPenceresi istek={nedenIstegi} onKapat={() => setNedenIstegi(null)} />
    </Modal>
  );
}

// --- Haftalık plan ----------------------------------------------------------------------------
export function HaftalikPlan({ secenekler, yazabilir = true }) {
  const api = useHarcirahApi('/harcirah-rezervasyon');
  const { message } = App.useApp();
  const [hafta, setHafta] = useState(() => pazartesi(dayjs()));
  const [nedenIstegi, setNedenIstegi] = useState(null);
  const [veri, setVeri] = useState(null);
  const [yukleniyor, setYukleniyor] = useState(false);
  const [pencere, setPencere] = useState({ acik: false, id: null });
  const genis = Grid.useBreakpoint().md;

  const yukle = useCallback(() => {
    setYukleniyor(true);
    api.get('/hafta', { bas: hafta.format('YYYY-MM-DD') })
      .then(setVeri).catch((e) => message.error(hata(e))).finally(() => setYukleniyor(false));
  }, [api, hafta, message]);
  useEffect(() => { yukle(); }, [yukle]);

  const gunler = useMemo(() => [...Array(7)].map((_, i) => hafta.add(i, 'day')), [hafta]);
  const ozelGun = useMemo(() => Object.fromEntries((veri?.ozel_gunler || []).map((o) => [o.tarih, o])), [veri]);

  // Satır = personel; aynı hafta içinde birden çok seyahati olabilir
  const satirlar = useMemo(() => {
    const kisiler = new Map();
    (veri?.seyahatler || []).forEach((s) => {
      if (!kisiler.has(s.kullanici_no)) {
        kisiler.set(s.kullanici_no, { key: s.kullanici_no, personel_adi: s.personel_adi, seyahatler: [], gun: {} });
      }
      const k = kisiler.get(s.kullanici_no);
      k.seyahatler.push(s);
      s.gunler.forEach((g) => { k.gun[g.tarih] = { ...g, seyahat: s }; });
    });
    return [...kisiler.values()].map((k) => ({
      ...k,
      haftaToplam: gunler.reduce((t, d) => t + (k.gun[d.format('YYYY-MM-DD')] ? gunTutari(k.gun[d.format('YYYY-MM-DD')]) : 0), 0),
    }));
  }, [veri, gunler]);

  const ogunDegistir = (g, ogun) => {
    const yeni = !g[ogun];
    setNedenIstegi({
      baslik: `${tarih(g.tarih)} ${OGUN_ADI[ogun]} hakkını ${yeni ? 'aç' : 'kapat'}`,
      aciklama: `${g.seyahat.personel_adi} · ${g.seyahat.il_ad}`,
      onOk: (neden) => api.put(`/${g.seyahat.id}/ogun`, { tarih: g.tarih, ogun, deger: yeni, neden })
        .then(() => yukle())
        .catch((e) => { message.error(hata(e)); if (e?.response?.status === 409) yukle(); }),
    });
  };

  const OgunDugmesi = ({ g, ogun }) => {
    const acik = !!g[ogun];
    const t = g[`${ogun}_tutar`];
    const kilitli = !yazabilir || !g.seyahat.duzenlenebilir;
    const durum = !acik ? 'hak yok' : t != null ? `çalışan ${para(t)} girdi` : 'hak var, tutar girilmedi';
    return (
      <Tooltip title={`${OGUN_ADI[ogun]}: ${durum}${kilitli ? '' : ' — hakkı açıp kapatmak için tıklayın'}`}>
        <Button size="small" type={acik ? 'primary' : 'default'} ghost={acik && t == null}
          style={{ width: 30, padding: 0, opacity: kilitli && !acik ? 0.5 : 1 }}
          onClick={kilitli ? undefined : () => ogunDegistir(g, ogun)}>
          {ogun === 'ogle' ? 'Ö' : 'A'}
        </Button>
      </Tooltip>
    );
  };

  const kolonlar = [
    {
      title: 'Personel', dataIndex: 'personel_adi', fixed: genis ? 'left' : undefined, width: genis ? 250 : 170,
      render: (ad, r) => (
        <div>
          <Text strong>{ad}</Text>
          {r.seyahatler.map((s) => (
            <div key={s.id}>
              <Tag color={DURUM_RENK[s.durum]} style={{ cursor: 'pointer', marginTop: 4 }}
                onClick={() => setPencere({ acik: true, id: s.id })}>
                {s.il_ad} · {kisaTarih(s.baslangic)}–{tarih(s.bitis)} · {s.tip}
              </Tag>
              <Text type="secondary" style={{ fontSize: 11 }}>
                {s.seyahat_turu_adi} · {s.durum !== 'taslak' ? `${s.durum_adi} · ` : ''}{s.girilen_ogun}/{s.acik_ogun} Öğün
              </Text>
            </div>
          ))}
        </div>
      ),
    },
    ...gunler.map((d, i) => {
      const t = d.format('YYYY-MM-DD');
      const oz = ozelGun[t];
      return {
        title: (
          <div style={{ lineHeight: 1.2 }}>
            <div>{GUNLER[i]} {d.format('DD.MM')}</div>
            {oz && <Tooltip title={oz.ad}><Tag color="orange" style={{ marginTop: 2, fontSize: 10 }}>{oz.yarim_gun ? 'Arife' : 'Tatil'}</Tag></Tooltip>}
          </div>
        ),
        key: t, align: 'center', width: 112,
        onCell: (r) => {
          const g = r.gun[t];
          const ciddi = g && ['ogle', 'aksam'].some((o) => ciddiMi(g.seyahat.isaretler?.[`${t}|${o}`]));
          return ciddi ? { className: 'hr-isaretli-hucre' } : {};
        },
        render: (_, r) => {
          const g = r.gun[t];
          if (!g) return <Text type="secondary">—</Text>;
          const tutarlar = ['ogle', 'aksam'].filter((o) => g[o]).map((o) => (g[`${o}_tutar`] != null ? kisaPara(g[`${o}_tutar`]) : '·'));
          const isaretler = ['ogle', 'aksam'].flatMap((o) => g.seyahat.isaretler?.[`${g.tarih}|${o}`] || []);
          const ciddi = isaretler.filter((i) => ['sehir', 'tarih', 'ayni'].includes(i.tur));
          return (
            <div>
              <Space size={4}><OgunDugmesi g={g} ogun="ogle" /><OgunDugmesi g={g} ogun="aksam" /></Space>
              <div style={{ fontSize: 11, marginTop: 2, whiteSpace: 'nowrap' }}>
                {gunTutari(g) ? <Text strong style={{ fontSize: 11 }}>{para(gunTutari(g))}</Text>
                  : <Text type="secondary" style={{ fontSize: 11 }}>{tutarlar.length ? 'girilmedi' : ''}</Text>}
              </div>
              {ciddi.length > 0 && (
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <IsaretEtiketleri isaretler={ciddi.filter((i, n) => ciddi.findIndex((x) => x.tur === i.tur) === n)} kisa />
                </div>
              )}
            </div>
          );
        },
      };
    }),
    { title: 'Hafta Toplamı', dataIndex: 'haftaToplam', align: 'right', width: 130, fixed: genis ? 'right' : undefined, render: (v) => <Text strong>{para(v)}</Text> },
  ];

  const genelToplam = satirlar.reduce((t, r) => t + r.haftaToplam, 0);

  return (
    <div>
      <Space wrap style={{ marginBottom: 12 }}>
        <Button icon={<LeftOutlined />} onClick={() => setHafta(hafta.subtract(7, 'day'))} />
        <DatePicker value={hafta} allowClear={false} format={() => `${hafta.format('DD.MM')} – ${hafta.add(6, 'day').format('DD.MM.YYYY')}`}
          onChange={(d) => d && setHafta(pazartesi(d))} />
        <Button icon={<RightOutlined />} onClick={() => setHafta(hafta.add(7, 'day'))} />
        <Button onClick={() => setHafta(pazartesi(dayjs()))}>Bu Hafta</Button>
        <Button icon={<ReloadOutlined />} onClick={yukle} />
        {yazabilir && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setPencere({ acik: true, id: null })}>Seyahat Ekle</Button>
        )}
      </Space>
      <Table
        size="small"
        bordered
        loading={yukleniyor}
        columns={kolonlar}
        dataSource={satirlar}
        pagination={false}
        scroll={{ x: 1130 }}
        locale={{ emptyText: <Empty description="Bu hafta seyahat yok" /> }}
        summary={() => (satirlar.length ? (
          <Table.Summary fixed>
            <Table.Summary.Row>
              <Table.Summary.Cell index={0}><Text strong>Toplam ({satirlar.length} Kişi)</Text></Table.Summary.Cell>
              {gunler.map((d, i) => {
                const t = d.format('YYYY-MM-DD');
                const top = satirlar.reduce((a, r) => a + (r.gun[t] ? gunTutari(r.gun[t]) : 0), 0);
                return <Table.Summary.Cell key={t} index={i + 1} align="center"><Text style={{ fontSize: 12 }}>{top ? para(top) : ''}</Text></Table.Summary.Cell>;
              })}
              <Table.Summary.Cell index={8} align="right"><Text strong>{para(genelToplam)}</Text></Table.Summary.Cell>
            </Table.Summary.Row>
          </Table.Summary>
        ) : null)}
      />
      <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
        Ö = öğle, A = akşam. Dolu düğme: çalışan tutarı girdi · çerçeveli: hak var, tutar bekleniyor · boş: hak yok.
        {yazabilir ? ' Düğmeye tıklayınca hak açılır/kapanır (neden sorulur).' : ' Planı ve öğün haklarını personel kendi ekranından girer.'}
        {' '}Etikete tıklayınca seyahat ve fişler açılır.
      </Text>
      <SeyahatPenceresi
        acik={pencere.acik}
        seyahatId={pencere.id}
        varsayilanHafta={hafta}
        secenekler={secenekler}
        yazabilir={yazabilir}
        onKapat={() => setPencere({ acik: false, id: null })}
        onKaydedildi={() => { setPencere({ acik: false, id: null }); yukle(); }}
      />
      <NedenPenceresi istek={nedenIstegi} onKapat={() => setNedenIstegi(null)} />
    </div>
  );
}

// --- Personel ---------------------------------------------------------------------------------
// Durum dağılımı renkleri: rapordaki onay durumu çubuğuyla aynı (durum paleti; taslak nötr gri)
const DURUM_PAYI = [
  { alan: 'onaylanan', ad: 'Onaylanan', renk: '#0ca30c' },
  { alan: 'bekleyen', ad: 'Onay Bekleyen', renk: '#fab219' },
  { alan: 'taslak', ad: 'Taslak / Geri', renk: '#a8a8a8' },
];
const basHarfler = (ad) => (ad || '?').split(/\s+/).filter(Boolean).map((x) => x[0]).slice(0, 2).join('').toLocaleUpperCase('tr');

function DurumPayi({ r }) {
  if (!r.toplam) return null;
  return (
    <Tooltip title={<div>{DURUM_PAYI.map((d) => <div key={d.alan}>{d.ad}: {para(r[d.alan])}</div>)}</div>}>
      <div style={{ display: 'flex', height: 6, borderRadius: 3, overflow: 'hidden', gap: 2, marginTop: 4, width: 140, marginLeft: 'auto' }}>
        {DURUM_PAYI.filter((d) => r[d.alan] > 0).map((d) => (
          <div key={d.alan} style={{ width: `${(r[d.alan] / r.toplam) * 100}%`, background: d.renk }} />
        ))}
      </div>
    </Tooltip>
  );
}

function KisiSeyahatleri({ kisi, onAc }) {
  const api = useHarcirahApi();
  const [liste, setListe] = useState(null);
  useEffect(() => { api.get(`/personel/${kisi}`).then(setListe).catch(() => setListe([])); }, [api, kisi]);
  if (!liste) return <div style={{ padding: 12, textAlign: 'center' }}><Spin size="small" /></div>;
  return (
    <Table size="small" rowKey="id" dataSource={liste} pagination={false} style={{ margin: '4px 0' }}
      onRow={(r) => ({ onClick: () => onAc(r.id), style: { cursor: 'pointer' } })}
      columns={[
        { title: 'Yer', dataIndex: 'il_ad', render: (v, r) => <span><b>{v}</b> <Tag style={{ marginLeft: 4 }}>{r.seyahat_turu_adi}</Tag>{r.aciklama && <div><Text type="secondary" style={{ fontSize: 12 }}>{r.aciklama}</Text></div>}</span> },
        { title: 'Tarih', render: (_, r) => <span>{tarih(r.baslangic)} – {tarih(r.bitis)}<div><Text type="secondary" style={{ fontSize: 12 }}>{r.gun} Gün · Tip {r.tip}</Text></div></span> },
        { title: 'Durum', dataIndex: 'durum', render: (v, r) => <Tag color={DURUM_RENK[v]}>{r.durum_adi}</Tag> },
        { title: 'Öğün', align: 'center', render: (_, r) => `${r.girilen_ogun} / ${r.acik_ogun}` },
        { title: 'İşaretler', dataIndex: 'isaret_sayilari', render: (v) => {
          const l = Object.entries(v || {}).map(([tur, n]) => ({ tur, n }));
          return l.length ? <IsaretEtiketleri kisa isaretler={l.map((x) => ({ tur: x.tur }))} /> : <Text type="secondary">—</Text>;
        } },
        { title: 'Yemek Harcırahı', dataIndex: 'toplam', align: 'right', render: (v) => <b>{para(v)}</b> },
        { title: 'Şirket Giderleri', dataIndex: 'gider_toplam', align: 'right', render: (v, r) => <span>{v ? para(v) : <Text type="secondary">—</Text>}{r.ulasim_adi && <div><Text type="secondary" style={{ fontSize: 12 }}>{r.ulasim_adi}</Text></div>}</span> },
      ]} />
  );
}

export function PersonelSekmesi({ secenekler }) {
  const api = useHarcirahApi();
  const { message } = App.useApp();
  const [liste, setListe] = useState([]);
  const [yukleniyor, setYukleniyor] = useState(false);
  const [ara, setAra] = useState('');
  const [pencere, setPencere] = useState(null);
  const [surum, setSurum] = useState(0);
  const yukle = useCallback(() => {
    setYukleniyor(true);
    api.get('/personel').then(setListe).catch((e) => message.error(hata(e))).finally(() => setYukleniyor(false));
  }, [api, message]);
  useEffect(() => { yukle(); }, [yukle]);

  const q = ara.trim().toLocaleLowerCase('tr');
  const gorunen = q ? liste.filter((r) => [r.personel_adi, r.pozisyon, r.amir_adi, r.il_ad]
    .some((v) => (v || '').toLocaleLowerCase('tr').includes(q))) : liste;
  const toplam = (alan) => liste.reduce((a, r) => a + (r[alan] || 0), 0);

  const ozet = [
    { ad: 'Kişi', deger: liste.length },
    { ad: 'Seyahat', deger: toplam('seyahat_sayisi') },
    { ad: 'Seyahat Günü', deger: toplam('gun_sayisi') },
    { ad: 'Yemek Harcırahı', deger: para(toplam('toplam')) },
    { ad: 'Onay Bekleyen', deger: para(toplam('bekleyen')) },
    { ad: 'Şirket Giderleri', deger: para(toplam('gider_toplam')) },
  ];

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 12 }}>
        {ozet.map((o) => (
          <div key={o.ad} className="hr-ozet-kart">
            <Text type="secondary" style={{ fontSize: 12 }}>{o.ad}</Text>
            <div style={{ fontSize: 20, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{o.deger}</div>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
        <Input.Search allowClear placeholder="Ad, Pozisyon, Amir Ya Da İl Ara" style={{ maxWidth: 320 }} value={ara} onChange={(e) => setAra(e.target.value)} />
        <Space size={12} wrap>
          {DURUM_PAYI.map((d) => (
            <Space key={d.alan} size={4}><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: d.renk }} /><Text type="secondary" style={{ fontSize: 12 }}>{d.ad}</Text></Space>
          ))}
          <Text type="secondary" style={{ fontSize: 12 }}>· Tip, amir ve il kişinin son seyahatinden gelir.</Text>
        </Space>
      </div>
      <Table
        size="middle" loading={yukleniyor} rowKey="kullanici_no" dataSource={gorunen} pagination={false}
        scroll={{ x: 1120 }}
        expandable={{
          expandedRowRender: (r) => <KisiSeyahatleri key={`${r.kullanici_no}-${surum}`} kisi={r.kullanici_no} onAc={setPencere} />,
          rowExpandable: (r) => r.seyahat_sayisi > 0,
        }}
        locale={{ emptyText: <Empty description={q ? 'Aramaya uyan kişi yok' : 'Henüz seyahat girilmedi'} /> }}
        columns={[
          {
            title: 'Personel', dataIndex: 'personel_adi', sorter: (a, b) => (a.personel_adi || '').localeCompare(b.personel_adi || '', 'tr'),
            render: (v, r) => (
              <Space size={10}>
                <Avatar style={{ background: r.aktif ? '#1677ff' : '#a8a8a8', flexShrink: 0 }}>{basHarfler(v)}</Avatar>
                <div style={{ lineHeight: 1.3 }}>
                  <Text strong>{v}</Text>{!r.aktif && <Tag style={{ marginLeft: 6 }}>Pasif</Tag>}
                  <div><Text type="secondary" style={{ fontSize: 12 }}>{r.pozisyon || '—'}</Text></div>
                </div>
              </Space>
            ),
          },
          {
            title: 'Tip', dataIndex: 'tip', width: 150,
            render: (v) => <Tag color={TIP_RENK[v]} style={{ marginInlineEnd: 0 }}>{v} · {TIP_ADI[v]}</Tag>,
          },
          { title: 'Amir', dataIndex: 'amir_adi', render: (v) => v || '—' },
          {
            title: 'Son Seyahat', dataIndex: 'son_baslangic', sorter: (a, b) => (a.son_baslangic || '').localeCompare(b.son_baslangic || ''),
            render: (_, r) => (
              <div style={{ lineHeight: 1.35 }}>
                <Text strong>{r.il_ad}</Text> <Tag color={DURUM_RENK[r.son_durum]} style={{ marginLeft: 4 }}>{r.son_durum_adi}</Tag>
                <div><Text type="secondary" style={{ fontSize: 12 }}>{tarih(r.son_baslangic)} – {tarih(r.son_bitis)}</Text></div>
              </div>
            ),
          },
          {
            title: 'Seyahat', dataIndex: 'seyahat_sayisi', align: 'center', width: 110, sorter: (a, b) => a.seyahat_sayisi - b.seyahat_sayisi,
            render: (v, r) => <div style={{ lineHeight: 1.3 }}><b>{v}</b><div><Text type="secondary" style={{ fontSize: 12 }}>{r.gun_sayisi} Gün</Text></div></div>,
          },
          {
            title: 'Yemek Harcırahı', dataIndex: 'toplam', align: 'right', width: 170, sorter: (a, b) => a.toplam - b.toplam, defaultSortOrder: undefined,
            render: (v, r) => <div><Text strong style={{ fontSize: 15, fontVariantNumeric: 'tabular-nums' }}>{para(v)}</Text><DurumPayi r={r} /></div>,
          },
          {
            title: 'Şirket Giderleri', dataIndex: 'gider_toplam', align: 'right', width: 140, sorter: (a, b) => a.gider_toplam - b.gider_toplam,
            render: (v) => (v ? <Text style={{ fontVariantNumeric: 'tabular-nums' }}>{para(v)}</Text> : <Text type="secondary">—</Text>),
          },
        ]}
      />
      <SeyahatPenceresi acik={!!pencere} seyahatId={pencere} secenekler={secenekler} kok="/harcirah" yazabilir={false}
        onKapat={() => setPencere(null)} onKaydedildi={() => { setPencere(null); setSurum((x) => x + 1); yukle(); }} />
    </div>
  );
}
